// ABOUTME: Hook to convert NavigationTabsProps/SubrouteProps into MenuItemConfig arrays
// ABOUTME: Used by both the mobile overlay menu and the desktop "More" overflow menu
import { type MenuItemConfig } from "@hm/fundamental"
import { useCallback, useMemo } from "react"

import type { NavigationSubrouteProps, NavigationTabsProps } from "./types"

const convertSubroutesToMenuItems = (
	subroutes: NavigationSubrouteProps[],
): MenuItemConfig[] => {
	return subroutes.map(subroute => {
		const baseItem = {
			key: subroute.url,
			title: subroute.label,
			onClicked: (_key: string) => {
				subroute.onClick?.()
			},
			closeOnClick:
				!subroute.subroutes || subroute.subroutes.length === 0,
		}

		if (subroute.subroutes && subroute.subroutes.length > 0) {
			return {
				...baseItem,
				items: convertSubroutesToMenuItems(subroute.subroutes),
			} as MenuItemConfig
		}

		return baseItem as MenuItemConfig
	})
}

const convertTabsToMenuItems = (
	tabs: NavigationTabsProps[],
): MenuItemConfig[] => {
	return tabs.map(tab => {
		const baseItem = {
			key: tab.url,
			title: tab.label,
			onClicked: (_key: string) => {
				tab.onClick?.()
			},
			closeOnClick: !tab.subroutes || tab.subroutes.length === 0,
		}

		if (tab.subroutes && tab.subroutes.length > 0) {
			return {
				...baseItem,
				items: convertSubroutesToMenuItems(tab.subroutes),
			} as MenuItemConfig
		}

		return baseItem as MenuItemConfig
	})
}

interface UseMenuItemsResult {
	menuItems: MenuItemConfig[]
	overflowMenuItems: MenuItemConfig[]
	convertSubroutesToMenuItems: (
		subroutes: NavigationSubrouteProps[],
	) => MenuItemConfig[]
}

export const useMenuItems = (
	navigationTabs: NavigationTabsProps[] | undefined,
	overflowTabs: NavigationTabsProps[],
): UseMenuItemsResult => {
	const stableConvertSubroutes = useCallback(
		(subroutes: NavigationSubrouteProps[]) =>
			convertSubroutesToMenuItems(subroutes),
		[],
	)

	const menuItems = useMemo(() => {
		return navigationTabs ? convertTabsToMenuItems(navigationTabs) : []
	}, [navigationTabs])

	const overflowMenuItems = useMemo(() => {
		return convertTabsToMenuItems(overflowTabs)
	}, [overflowTabs])

	return {
		menuItems,
		overflowMenuItems,
		convertSubroutesToMenuItems: stableConvertSubroutes,
	}
}
